'use client';

import { OptimizedImage } from '@/components/optimized-image';
import { useT } from '@/components/i18n-provider';
import { cn } from '@/lib/utils';
import { Play, Pause, Volume2, VolumeX, FileText, Box, Download } from 'lucide-react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { Tip } from '@/components/ui/tooltip';

interface PostMediaProps {
  mediaUrl?: string | null;
  mediaType?: string | null;
  thumbnailUrl?: string | null;
  /** Alt text for images */
  alt?: string;
  /** Title (used as alt fallback) */
  title?: string;
  /** Compact mode for feed cards (max-h-96, click to navigate) */
  compact?: boolean;
  className?: string;
  /** Called when media is clicked (e.g. to open lightbox) */
  onMediaClick?: (e: React.MouseEvent) => void;
  /** Media dimensions from the API (enables accurate skeletons + portrait rendering) */
  mediaWidth?: number;
  mediaHeight?: number;
  /** Hint to load this image eagerly (above-the-fold) */
  priority?: boolean;
  /** Media URL has been prefetched into browser cache by the preloader */
  preloaded?: boolean;
}

/**
 * Detect effective media type from URL extension when mediaType is generic or missing.
 * Falls back to mediaType if no extension match.
 */
function detectMediaType(url: string | null | undefined, declaredType: string | null | undefined): string {
  if (!url) return declaredType || 'unknown';

  const ext = url.split('?')[0].split('.').pop()?.toLowerCase();

  // Video
  if (declaredType === 'video') return 'video';
  if (ext && ['mp4', 'webm', 'mov', 'avi', 'mkv', 'ogv'].includes(ext)) return 'video';

  // Audio
  if (declaredType === 'audio') return 'audio';
  if (ext && ['mp3', 'wav', 'ogg', 'flac', 'aac', 'wma', 'm4a', 'opus'].includes(ext)) return 'audio';

  // 3D models
  if (declaredType === '3d') return '3d';
  if (ext && ['glb', 'gltf', 'obj', 'fbx', 'stl', 'usdz'].includes(ext)) return '3d';

  // PDF / documents
  if (ext && ['pdf'].includes(ext)) return 'pdf';
  if (ext && ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv'].includes(ext)) return 'document';

  // GIF
  if (declaredType === 'gif' || ext === 'gif') return 'gif';

  // Image (default for known image extensions or declared type)
  if (declaredType === 'image') return 'image';
  if (ext && ['jpg', 'jpeg', 'png', 'webp', 'avif', 'bmp', 'svg', 'ico', 'tiff'].includes(ext)) return 'image';

  // Link type
  if (declaredType === 'link') return 'link';

  // For /api/content/ URLs with no extension and no declared type, signal unknown
  if (!declaredType && url.includes('/api/content/')) return 'unknown';

  // If we have a URL but can't determine type, assume image (most common)
  return declaredType || 'image';
}

/**
 * Map a Content-Type header value to our media type enum.
 */
function mimeToMediaType(mime: string): string {
  const base = mime.split(';')[0].trim().toLowerCase();
  if (base.startsWith('audio/')) return 'audio';
  if (base.startsWith('video/')) return 'video';
  if (base.startsWith('image/gif')) return 'gif';
  if (base.startsWith('image/')) return 'image';
  if (base.startsWith('model/')) return '3d';
  if (base === 'application/pdf') return 'pdf';
  return 'file';
}

/**
 * Video player with Twitter/X-style autoplay on scroll.
 * - Autoplays muted when >50% visible in viewport
 * - Pauses when scrolled out of view
 * - User can unmute by clicking the sound button
 * - Shows controls on hover/interaction
 */
function VideoPlayer({
  src,
  poster,
  compact,
  className,
  onClick,
  aspectRatio,
  portrait: portraitProp,
  preloaded,
}: {
  src: string;
  poster?: string;
  compact?: boolean;
  aspectRatio?: number;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  portrait?: boolean;
  preloaded?: boolean;
}) {
  const t = useT();
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const [muted, setMuted] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [metaLoaded, setMetaLoaded] = useState(false);
  const [detectedPortrait, setDetectedPortrait] = useState(false);
  const [detectedAR, setDetectedAR] = useState<number | undefined>(undefined);
  const isPortraitMode = portraitProp || detectedPortrait;
  const blurCanvasRef = useRef<HTMLCanvasElement>(null);
  const controlsTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // IntersectionObserver: autoplay when visible, pause when not
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Play/pause based on visibility
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isVisible) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [isVisible]);

  // Pause when lightbox opens
  useEffect(() => {
    const handler = () => {
      videoRef.current?.pause();
    };
    window.addEventListener('lightboxOpen', handler);
    return () => window.removeEventListener('lightboxOpen', handler);
  }, []);

  // Track time/state
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => setProgress(v.currentTime);
    const onDur = () => setDuration(v.duration || 0);
    const onPlay = () => setIsPaused(false);
    const onPause = () => setIsPaused(true);
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('durationchange', onDur);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    return () => {
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('durationchange', onDur);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
    };
  }, []);

  const resetHideTimer = useCallback(() => {
    setShowControls(true);
    if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    controlsTimerRef.current = setTimeout(() => setShowControls(false), 3000);
  }, []);

  const toggleMute = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setMuted((m) => !m);
    setHasInteracted(true);
    resetHideTimer();
  }, [resetHideTimer]);

  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    // If onClick handler is provided (lightbox), use it
    if (onClick) {
      onClick(e);
      return;
    }
    // Otherwise toggle play/pause
    const video = videoRef.current;
    if (!video) return;
    e.preventDefault();
    e.stopPropagation();
    if (video.paused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
    setHasInteracted(true);
    resetHideTimer();
  }, [onClick, resetHideTimer]);

  const handleMouseEnter = useCallback(() => {
    setShowControls(true);
    if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
  }, []);

  const handleMouseLeave = useCallback(() => {
    controlsTimerRef.current = setTimeout(() => setShowControls(false), 1500);
  }, []);

  const seek = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const bar = progressRef.current;
    const v = videoRef.current;
    if (!bar || !v || !duration) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    v.currentTime = ratio * duration;
    resetHideTimer();
  }, [duration, resetHideTimer]);

  const fmtTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const pct = duration > 0 ? (progress / duration) * 100 : 0;
  const controlsVisible = showControls || isPaused || !hasInteracted;

  const maxH = compact ? '24rem' : '37.5rem';

  // Draw a video frame onto the blur canvas when portrait is detected client-side and no poster exists
  useEffect(() => {
    if (!isPortraitMode || portraitProp || poster) return;
    const v = videoRef.current;
    const canvas = blurCanvasRef.current;
    if (!v || !canvas) return;
    let done = false;
    const draw = () => {
      if (done || !v.videoWidth) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      try {
        canvas.width = 32;
        canvas.height = Math.round(32 * v.videoHeight / v.videoWidth);
        ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
        done = true;
      } catch { /* tainted canvas */ }
    };
    draw();
    const onPlaying = () => requestAnimationFrame(draw);
    v.addEventListener('playing', onPlaying);
    const t1 = setTimeout(draw, 200);
    const t2 = setTimeout(draw, 600);
    return () => {
      v.removeEventListener('playing', onPlaying);
      clearTimeout(t1); clearTimeout(t2);
    };
  }, [isPortraitMode, portraitProp, poster]);

  const portraitStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectPosition: 'center',
    background: 'transparent',
  };

  const blurStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    filter: 'blur(24px)',
    transform: 'scale(1.1)',
    opacity: 0.5,
  };

  return (
    <div
      ref={containerRef}
      className={cn('relative cursor-pointer group overflow-hidden', isPortraitMode && !portraitProp && '@sm/main:bg-muted @sm/main:dark:bg-black', className)}
      style={detectedPortrait && !portraitProp ? { width: '100%', aspectRatio: detectedAR, maxHeight: maxH } : undefined}
      onClick={handleContainerClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Blurred background for client-detected portrait videos (hidden on narrow containers) */}
      {detectedPortrait && !portraitProp && poster && (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={poster} alt="" aria-hidden className="hidden @sm/main:block" style={blurStyle} />
      )}
      {detectedPortrait && !portraitProp && !poster && (
        <canvas ref={blurCanvasRef} aria-hidden className="hidden @sm/main:block" style={blurStyle} />
      )}
      {!portraitProp && !metaLoaded && (
        poster
          ? /* eslint-disable-next-line @next/next/no-img-element */
            <img src={poster} alt="" className="w-full object-cover" style={{ aspectRatio: aspectRatio || 16 / 9, maxHeight: maxH }} />
          : <div className="w-full bg-muted animate-pulse" style={{ aspectRatio: aspectRatio || 16 / 9, maxHeight: maxH }} />
      )}
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        muted={muted}
        loop
        playsInline
        // `metadata` (was `auto`) — only fetch the moov atom on mount.
        // Sample data is pulled on demand once the IntersectionObserver
        // calls `.play()`. Pairs with the upload-side fast-start remux
        // (lib/media/video-remux.ts) so the browser finds the moov in
        // the first ~64-512KB instead of hunting through the file.
        preload="metadata"
        className={cn(
          !isPortraitMode && cn('w-full object-cover', compact ? 'max-h-96' : 'max-h-[600px]'),
          isPortraitMode && 'object-cover @sm/main:object-contain',
          'transition-opacity duration-300',
          metaLoaded ? 'opacity-100' : (!isPortraitMode && 'opacity-0'),
        )}
        style={isPortraitMode ? portraitStyle : undefined}
        onLoadedMetadata={() => {
          setMetaLoaded(true);
          const v = videoRef.current;
          if (v && v.videoWidth && v.videoHeight && !aspectRatio) {
            const clientAR = v.videoWidth / v.videoHeight;
            if (clientAR < 1) {
              setDetectedPortrait(true);
              setDetectedAR(Math.max(clientAR, 3 / 4));
            }
          }
        }}
      />

      {/* Center play icon when paused */}
      {isPaused && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="h-14 w-14 rounded-full bg-black/60 flex items-center justify-center backdrop-blur-sm">
            <Play className="h-7 w-7 text-white fill-white ml-1" />
          </div>
        </div>
      )}

      {/* Bottom controls bar */}
      <div
        className={cn(
          'absolute bottom-0 left-0 right-0 transition-opacity duration-300',
          controlsVisible ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Progress bar */}
        <div
          ref={progressRef}
          className="h-5 flex items-end cursor-pointer px-2 pb-0.5"
          onClick={seek}
        >
          <div className="w-full h-[3px] bg-white/20 rounded-full relative group/bar hover:h-1.5 transition-all">
            <div
              className="absolute inset-y-0 left-0 bg-white rounded-full"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Controls row */}
        <div className="flex items-center justify-between px-2 pb-2 pt-0.5 bg-gradient-to-t from-black/60 to-transparent">
          <span className="text-[10px] text-white/60 tabular-nums">
            {fmtTime(progress)} / {fmtTime(duration)}
          </span>
          <Tip label={muted ? t('postMedia.unmute') : t('postMedia.mute')}>
            <button
              onClick={toggleMute}
              className="h-7 w-7 rounded-full bg-black/60 flex items-center justify-center backdrop-blur-sm"
            >
              {muted ? (
                <VolumeX className="h-3.5 w-3.5 text-white" />
              ) : (
                <Volume2 className="h-3.5 w-3.5 text-white" />
              )}
            </button>
          </Tip>
        </div>
      </div>
    </div>
  );
}

function formatTime(s: number): string {
  if (!isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// Global single-audio management
// ---------------------------------------------------------------------------
let _activeAudio: HTMLAudioElement | null = null;
function claimAudioFocus(el: HTMLAudioElement) {
  if (_activeAudio && _activeAudio !== el) _activeAudio.pause();
  _activeAudio = el;
}
export function pauseAllAudio() {
  if (_activeAudio) { _activeAudio.pause(); _activeAudio = null; }
}
export function getActiveAudioTime(): number {
  return _activeAudio?.currentTime ?? 0;
}
export function dispatchAudioSeek(src: string, time: number) {
  window.dispatchEvent(new CustomEvent('robutler-audio-seek', { detail: { src, time } }));
}

// ---------------------------------------------------------------------------
// Canvas frequency visualizer (real-time via Web Audio AnalyserNode)
// ---------------------------------------------------------------------------
function drawSpectrogram(
  canvas: HTMLCanvasElement,
  analyser: AnalyserNode,
  opts: { thumbnail: boolean; dark: boolean; frozen?: Uint8Array | null },
) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  const w = rect.width;
  const h = rect.height;
  const cw = Math.round(w * dpr);
  const ch = Math.round(h * dpr);
  if (canvas.width !== cw || canvas.height !== ch) {
    canvas.width = cw;
    canvas.height = ch;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  ctx.clearRect(0, 0, w, h);

  const freq = opts.frozen ?? new Uint8Array(analyser.frequencyBinCount);
  if (!opts.frozen) analyser.getByteFrequencyData(freq as Uint8Array<ArrayBuffer>);

  const barCount = Math.min(Math.floor(w / 5), 72);
  const barW = w / barCount;
  const gap = Math.max(1, barW * 0.22);
  const radius = Math.min(2, (barW - gap) / 2);

  if (opts.thumbnail) {
    // White bars growing upward from bottom
    for (let i = 0; i < barCount; i++) {
      const bin = Math.floor(Math.pow(i / barCount, 1.5) * freq.length * 0.75);
      const val = freq[bin] / 255;
      const bh = Math.max(1, val * h * 0.92);
      const x = i * barW + gap / 2;
      const bw = barW - gap;
      ctx.fillStyle = `rgba(255,255,255,${0.2 + val * 0.8})`;
      ctx.beginPath();
      ctx.roundRect(x, h - bh, bw, bh, radius);
      ctx.fill();
    }
    return;
  }

  // Bars growing upward from a low baseline, with faint reflection below
  const center = h * 0.72;
  const isDark = opts.dark || document.documentElement.classList.contains('dark');

  for (let i = 0; i < barCount; i++) {
    const bin = Math.floor(Math.pow(i / barCount, 1.5) * freq.length * 0.75);
    const val = freq[bin] / 255;
    const bh = Math.max(0.5, val * center * 0.88);
    const x = i * barW + gap / 2;
    const bw = barW - gap;
    const t = i / barCount;

    if (isDark) {
      const hue = 270 - t * 80;
      const alpha = 0.15 + val * 0.85;
      ctx.fillStyle = `hsla(${hue},80%,72%,${alpha})`;
      ctx.beginPath();
      ctx.roundRect(x, center - bh, bw, bh, radius);
      ctx.fill();
      ctx.fillStyle = `hsla(${hue},80%,72%,${alpha * 0.18})`;
      ctx.beginPath();
      ctx.roundRect(x, center + 2, bw, bh * 0.45, radius);
      ctx.fill();
    } else {
      const hue = 260 - t * 60;
      const alpha = 0.12 + val * 0.78;
      ctx.fillStyle = `hsla(${hue},55%,42%,${alpha})`;
      ctx.beginPath();
      ctx.roundRect(x, center - bh, bw, bh, radius);
      ctx.fill();
      ctx.fillStyle = `hsla(${hue},55%,42%,${alpha * 0.12})`;
      ctx.beginPath();
      ctx.roundRect(x, center + 2, bw, bh * 0.35, radius);
      ctx.fill();
    }
  }
}

function generateStaticBars(src: string, count: number): number[] {
  let h = 0;
  for (let i = 0; i < src.length; i++) h = ((h << 5) - h + src.charCodeAt(i)) | 0;
  const seed = (n: number) => {
    let s = (h ^ (n * 2654435761)) | 0;
    s = Math.imul(s ^ (s >>> 16), 0x45d9f3b);
    s = Math.imul(s ^ (s >>> 13), 0x45d9f3b);
    return ((s ^ (s >>> 16)) >>> 0) / 4294967296;
  };
  const bars: number[] = [];
  for (let i = 0; i < count; i++) {
    const t = i / count;
    const rolloff = 1 - t * 0.55;
    const o1 = Math.sin(t * 6.28 * 2.1 + seed(0) * 30) * 0.3;
    const o2 = Math.sin(t * 6.28 * 5.7 + seed(1) * 30) * 0.18;
    const o3 = Math.sin(t * 6.28 * 11.3 + seed(2) * 30) * 0.12;
    const noise = (seed(i + 100) - 0.5) * 0.25;
    const burst = seed(i + 200) > 0.88 ? seed(i + 300) * 0.35 : 0;
    const raw = 0.35 + (o1 + o2 + o3 + noise + burst) * rolloff;
    bars.push(Math.max(0.06, Math.min(1, raw)));
  }
  return bars;
}

/**
 * Draw a colorful static visualization to canvas using the same visual style
 * as the real-time spectrogram. Used as the initial/idle state before the user
 * presses play and the AnalyserNode is created.
 */
function drawStaticVis(
  canvas: HTMLCanvasElement,
  bars: number[],
  opts: { thumbnail: boolean; dark: boolean },
) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  const w = rect.width;
  const h = rect.height;
  if (w === 0 || h === 0) return;
  const cw = Math.round(w * dpr);
  const ch = Math.round(h * dpr);
  if (canvas.width !== cw || canvas.height !== ch) {
    canvas.width = cw;
    canvas.height = ch;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  ctx.clearRect(0, 0, w, h);

  const barCount = bars.length;
  const barW = w / barCount;
  const gap = Math.max(1, barW * 0.22);
  const radius = Math.min(2, (barW - gap) / 2);

  if (opts.thumbnail) {
    for (let i = 0; i < barCount; i++) {
      const val = bars[i];
      const bh = Math.max(1, val * h * 0.7);
      const x = i * barW + gap / 2;
      const bw = barW - gap;
      ctx.fillStyle = `rgba(255,255,255,${0.08 + val * 0.25})`;
      ctx.beginPath();
      ctx.roundRect(x, h - bh, bw, bh, radius);
      ctx.fill();
    }
    return;
  }

  const center = h * 0.72;
  const isDark = opts.dark || document.documentElement.classList.contains('dark');

  for (let i = 0; i < barCount; i++) {
    const val = bars[i];
    const bh = Math.max(0.5, val * center * 0.65);
    const x = i * barW + gap / 2;
    const bw = barW - gap;
    const t = i / barCount;

    if (isDark) {
      const hue = 270 - t * 80;
      ctx.fillStyle = `hsla(${hue},70%,65%,${0.08 + val * 0.2})`;
      ctx.beginPath();
      ctx.roundRect(x, center - bh, bw, bh, radius);
      ctx.fill();
      ctx.fillStyle = `hsla(${hue},70%,65%,${(0.08 + val * 0.2) * 0.15})`;
      ctx.beginPath();
      ctx.roundRect(x, center + 2, bw, bh * 0.4, radius);
      ctx.fill();
    } else {
      const hue = 260 - t * 60;
      ctx.fillStyle = `hsla(${hue},40%,50%,${0.06 + val * 0.16})`;
      ctx.beginPath();
      ctx.roundRect(x, center - bh, bw, bh, radius);
      ctx.fill();
      ctx.fillStyle = `hsla(${hue},40%,50%,${(0.06 + val * 0.16) * 0.1})`;
      ctx.beginPath();
      ctx.roundRect(x, center + 2, bw, bh * 0.3, radius);
      ctx.fill();
    }
  }
}

// ---------------------------------------------------------------------------
// AudioPlayer component
// ---------------------------------------------------------------------------
interface AudioPlayerProps {
  src: string;
  thumbnail?: string | null;
  compact?: boolean;
  dark?: boolean;
  autoPlay?: boolean;
  startTime?: number;
  className?: string;
  onMediaClick?: (e: React.MouseEvent) => void;
}

function AudioPlayer({ src, thumbnail, compact, dark, autoPlay, startTime, className, onMediaClick }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const rafRef = useRef<number>(0);
  const frozenFreqRef = useRef<Uint8Array | null>(null);

  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [hasAnalyser, setHasAnalyser] = useState(false);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const hasThumbnail = !!thumbnail;
  const isDark = !!dark;

  // Fallback static bars
  const staticBars = useRef<number[]>([]);
  if (staticBars.current.length === 0) staticBars.current = generateStaticBars(src, 64);

  // ---- Web Audio API setup (called on first user play) ----
  const ensureAnalyser = useCallback(() => {
    if (analyserRef.current || !audioRef.current) return;
    try {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const an = ctx.createAnalyser();
      an.fftSize = 256;
      an.smoothingTimeConstant = 0.76;
      const source = ctx.createMediaElementSource(audioRef.current);
      source.connect(an);
      an.connect(ctx.destination);
      audioCtxRef.current = ctx;
      analyserRef.current = an;
      sourceRef.current = source;
      setHasAnalyser(true);
    } catch { /* CORS / unsupported — falls back to static bars */ }
  }, []);

  // ---- Audio element events ----
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onMeta = () => setDuration(a.duration);
    const onTime = () => setCurrentTime(a.currentTime);
    const onPause = () => setPlaying(false);
    const onEnded = () => setPlaying(false);
    a.addEventListener('loadedmetadata', onMeta);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('pause', onPause);
    a.addEventListener('ended', onEnded);
    return () => { a.removeEventListener('loadedmetadata', onMeta); a.removeEventListener('timeupdate', onTime); a.removeEventListener('pause', onPause); a.removeEventListener('ended', onEnded); };
  }, []);

  // ---- Listen for external seek events (e.g. lightbox -> card sync) ----
  useEffect(() => {
    const handler = (e: Event) => {
      const { src: seekSrc, time } = (e as CustomEvent).detail;
      if (seekSrc === src && audioRef.current) {
        audioRef.current.currentTime = time;
        setCurrentTime(time);
      }
    };
    window.addEventListener('robutler-audio-seek', handler);
    return () => window.removeEventListener('robutler-audio-seek', handler);
  }, [src]);

  // ---- Auto-play with deferred analyser setup ----
  useEffect(() => {
    if (!autoPlay || !audioRef.current) return;
    const a = audioRef.current;
    if (startTime && startTime > 0) a.currentTime = startTime;
    a.play().then(() => {
      setPlaying(true);
      claimAudioFocus(a);
      // Audio is playing via default output. Now try to attach the Web Audio
      // analyser for visualization. We create the AudioContext first, attempt
      // resume(), and only call createMediaElementSource once confirmed running
      // so we never silence audio that's already playing.
      if (analyserRef.current) return;
      try {
        const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const wire = () => {
          if (analyserRef.current) { ctx.close().catch(() => {}); return; }
          const an = ctx.createAnalyser();
          an.fftSize = 256;
          an.smoothingTimeConstant = 0.76;
          const source = ctx.createMediaElementSource(a);
          source.connect(an);
          an.connect(ctx.destination);
          audioCtxRef.current = ctx;
          analyserRef.current = an;
          sourceRef.current = source;
          setHasAnalyser(true);
        };
        if (ctx.state === 'running') { wire(); }
        else { ctx.resume().then(() => { if (ctx.state === 'running') wire(); else ctx.close().catch(() => {}); }).catch(() => ctx.close().catch(() => {})); }
      } catch { /* CORS / unsupported */ }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Draw initial static visualization on canvas (before analyser) ----
  useEffect(() => {
    if (hasAnalyser) return;
    const canvas = canvasRef.current;
    if (canvas) drawStaticVis(canvas, staticBars.current, { thumbnail: hasThumbnail, dark: isDark });
  });

  // ---- Canvas animation loop (real-time when analyser ready) ----
  useEffect(() => {
    if (!hasAnalyser) return;
    let active = true;
    const loop = () => {
      if (!active) return;
      const canvas = canvasRef.current;
      const an = analyserRef.current;
      if (canvas && an) {
        if (playing) {
          const live = new Uint8Array(an.frequencyBinCount);
          an.getByteFrequencyData(live);
          const hasSignal = live.some(v => v > 0);
          if (hasSignal) frozenFreqRef.current = live;
          drawSpectrogram(canvas, an, { thumbnail: hasThumbnail, dark: isDark });
        } else if (frozenFreqRef.current) {
          drawSpectrogram(canvas, an, { thumbnail: hasThumbnail, dark: isDark, frozen: frozenFreqRef.current });
        }
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    loop();
    return () => { active = false; cancelAnimationFrame(rafRef.current); };
  }, [hasAnalyser, hasThumbnail, isDark, playing]);

  // ---- Cleanup AudioContext ----
  useEffect(() => () => { audioCtxRef.current?.close().catch(() => {}); }, []);

  // ---- Controls ----
  const togglePlay = (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    const a = audioRef.current;
    if (!a) return;
    ensureAnalyser();
    if (audioCtxRef.current?.state === 'suspended') audioCtxRef.current.resume();
    if (playing) { a.pause(); } else { claimAudioFocus(a); a.play().catch(() => {}); setPlaying(true); }
  };

  const handleSeek = (e: React.MouseEvent) => {
    e.stopPropagation();
    const wrapper = progressRef.current;
    const a = audioRef.current;
    if (!wrapper || !a || !duration) return;
    const rect = wrapper.getBoundingClientRect();
    a.currentTime = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)) * duration;
    setCurrentTime(a.currentTime);
  };

  const toggleMute = (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); if (audioRef.current) { audioRef.current.muted = !muted; setMuted(!muted); } };
  const handleVolume = (e: React.ChangeEvent<HTMLInputElement>) => { const v = parseFloat(e.target.value); setVolume(v); if (audioRef.current) { audioRef.current.volume = v; audioRef.current.muted = v === 0; setMuted(v === 0); } };

  // ---- Shared sub-components ----
  const playBtn = (size: 'sm' | 'lg', white?: boolean) => (
    <button
      onClick={togglePlay}
      className={cn(
        'rounded-full flex items-center justify-center shrink-0 cursor-pointer transition-all',
        white || isDark ? 'bg-white text-black hover:bg-white/90' : 'bg-foreground text-background hover:opacity-90',
        size === 'sm' ? 'h-8 w-8' : 'h-10 w-10',
      )}
    >
      {playing
        ? <Pause className={size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
        : <Play className={cn(size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4', 'ml-0.5')} />}
    </button>
  );

  const timeLabel = (t: number, white?: boolean) => (
    <span className={cn('text-[11px] font-mono tabular-nums select-none', white || isDark ? 'text-white/55' : 'text-muted-foreground')}>
      {formatTime(t)}
    </span>
  );

  const progressBar = (white?: boolean) => (
    <div
      ref={progressRef}
      onClick={handleSeek}
      className={cn('flex-1 relative cursor-pointer py-2 -my-2')}
    >
      <div className={cn('h-[3px] rounded-full overflow-hidden', white || isDark ? 'bg-white/15' : 'bg-foreground/10')}>
        <div
          className={cn('h-full rounded-full transition-[width] duration-150', white || isDark ? 'bg-white/70' : 'bg-foreground/45')}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );

  const volumeCtrl = (white?: boolean) => (
    <div className="hidden sm:flex items-center gap-1.5">
      <button onClick={toggleMute} className={cn('cursor-pointer transition-colors', white || isDark ? 'text-white/50 hover:text-white' : 'text-muted-foreground hover:text-foreground')}>
        {muted || volume === 0 ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
      </button>
      <input type="range" min="0" max="1" step="0.05" value={muted ? 0 : volume} onChange={handleVolume} onClick={(e) => e.stopPropagation()} className={cn('w-14 h-1 cursor-pointer', white || isDark ? 'accent-white' : 'accent-foreground')} />
    </div>
  );

  // ==================================================================
  //  WITH THUMBNAIL — album art + frequency overlay + controls
  // ==================================================================
  if (hasThumbnail) {
    return (
      <div className={cn('relative overflow-hidden', compact ? 'rounded-none -mx-3 sm:-mx-4' : 'rounded-xl', className)}>
        <audio ref={audioRef} preload="metadata" src={src} />
        <div className={cn('relative', compact ? 'aspect-[2/1]' : 'aspect-[5/2]')}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={thumbnail} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
          <div
            className={cn('absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-black/5', onMediaClick && 'cursor-pointer')}
            onClick={onMediaClick ? (e) => { e.stopPropagation(); onMediaClick(e); } : undefined}
          />

          {/* Frequency bars (static preview or real-time) — click opens lightbox */}
          <div
            className={cn('absolute left-0 right-0 px-2 sm:px-3', onMediaClick && 'cursor-pointer', compact ? 'bottom-[2.3rem] h-10 sm:h-12' : 'bottom-[2.75rem] h-14 sm:h-16')}
            onClick={onMediaClick ? (e) => { e.stopPropagation(); onMediaClick(e); } : undefined}
          >
            <canvas ref={canvasRef} className="w-full h-full pointer-events-none" />
          </div>

          {/* Controls */}
          <div className={cn('absolute bottom-0 left-0 right-0 flex items-center gap-2 sm:gap-3 px-3 sm:px-4', compact ? 'pb-2' : 'pb-3')}>
            {playBtn(compact ? 'sm' : 'lg', true)}
            {timeLabel(currentTime, true)}
            {progressBar(true)}
            {timeLabel(duration, true)}
            {volumeCtrl(true)}
          </div>
        </div>
      </div>
    );
  }

  // ==================================================================
  //  NO THUMBNAIL — full-width spectrogram + controls
  // ==================================================================
  return (
    <div className={cn('relative overflow-hidden', compact ? 'rounded-none -mx-3 sm:-mx-4' : 'rounded-xl', className)}>
      <audio ref={audioRef} preload="metadata" src={src} />
      <div className={cn('relative', isDark ? 'bg-white/[0.03]' : 'bg-muted/40 dark:bg-muted/20', compact ? 'p-3 sm:p-4' : 'p-4 sm:p-6')}>
        {/* Visualization (static preview or real-time) — click opens lightbox */}
        <div
          className={cn(onMediaClick && 'cursor-pointer', compact ? 'h-20 sm:h-24' : 'h-28 sm:h-36')}
          onClick={onMediaClick ? (e) => { e.stopPropagation(); onMediaClick(e); } : undefined}
        >
          <canvas ref={canvasRef} className="w-full h-full pointer-events-none" />
        </div>

        {/* Controls */}
        <div className={cn('relative flex items-center gap-2 sm:gap-3', compact ? 'mt-1' : 'mt-1.5')}>
          {playBtn(compact ? 'sm' : 'lg')}
          {timeLabel(currentTime)}
          {progressBar()}
          {timeLabel(duration)}
          {volumeCtrl()}
        </div>
      </div>
    </div>
  );
}

function ModelViewer3D({
  src,
  poster,
  compact,
  className,
  onClickOnly,
}: {
  src: string;
  poster?: string;
  compact?: boolean;
  className?: string;
  onClickOnly?: (e: React.MouseEvent) => void;
}) {
  const t = useT();
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const pointerStart = useRef<{ x: number; y: number; time: number } | null>(null);
  const viewerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = viewerRef.current;
    if (!el) return;
    const onError = () => setLoadError(true);
    el.addEventListener('error', onError);
    return () => el.removeEventListener('error', onError);
  }, [loaded]);

  // Distinguish click (no drag) from drag (model rotation)
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    pointerStart.current = { x: e.clientX, y: e.clientY, time: Date.now() };
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!pointerStart.current || !onClickOnly) return;
    const dx = Math.abs(e.clientX - pointerStart.current.x);
    const dy = Math.abs(e.clientY - pointerStart.current.y);
    const dt = Date.now() - pointerStart.current.time;
    pointerStart.current = null;
    if (dx < 6 && dy < 6 && dt < 300) {
      onClickOnly(e as unknown as React.MouseEvent);
    }
  }, [onClickOnly]);

  if (loadError) {
    return (
      <div className={cn('flex flex-col items-center justify-center gap-2 aspect-video bg-muted/50 rounded-xl text-muted-foreground', className)}>
        <Box className="h-8 w-8" />
        <span className="text-sm">{t('postMedia.model3dFailed')}</span>
      </div>
    );
  }

  if (compact && !loaded) {
    return (
      <div
        className={cn('relative cursor-pointer aspect-video bg-gradient-to-br from-blue-500/10 to-purple-500/10', className)}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setLoaded(true);
        }}
      >
        {poster ? (
          <OptimizedImage src={poster} alt={t('postMedia.model3dPreview')} fill className="object-cover" />
        ) : (
          /* @ts-expect-error model-viewer is a web component */
          <model-viewer
            ref={viewerRef}
            src={src}
            auto-rotate
            interaction-prompt="none"
            shadow-intensity="1"
            environment-image="neutral"
            exposure="1.2"
            tone-mapping="agx"
            loading="lazy"
            style={{ width: '100%', height: '100%', position: 'absolute', inset: 0, pointerEvents: 'none' }}
          />
        )}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="h-14 w-14 rounded-full bg-black/60 flex items-center justify-center backdrop-blur-sm">
            <Box className="h-7 w-7 text-white" />
          </div>
        </div>
        <div className="absolute bottom-2 right-2 text-xs bg-black/60 text-white px-2 py-1 rounded-full backdrop-blur-sm pointer-events-none">
          3D Model
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn('relative aspect-video', className)}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
    >
      {/* @ts-expect-error model-viewer is a web component */}
      <model-viewer
        ref={viewerRef}
        src={src}
        poster={poster}
        alt={t('contentPreview.model3d')}
        auto-rotate
        camera-controls
        shadow-intensity="1"
        environment-image="neutral"
        exposure="1.2"
        tone-mapping="agx"
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  );
}

function PdfEmbed({ src, compact, className }: { src: string; compact?: boolean; className?: string }) {
  const t = useT();
  if (compact) {
    return (
      <div className={cn('flex items-center gap-3 p-4 bg-muted/50 rounded-xl', className)}>
        <div className="h-12 w-12 rounded-lg bg-red-500/10 flex items-center justify-center shrink-0">
          <FileText className="h-6 w-6 text-red-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{t('postMedia.pdfDocument')}</p>
          <p className="text-xs text-muted-foreground">{t('postMedia.clickToView')}</p>
        </div>
      </div>
    );
  }

  return (
    <iframe
      src={src}
      className={cn('w-full border-0 rounded-xl', className)}
      style={{ height: '600px' }}
      title={t('postMedia.pdfDocumentTitle')}
    />
  );
}

function DocumentLink({ src, className }: { src: string; className?: string }) {
  const filename = src.split('/').pop()?.split('?')[0] || 'Document';
  const ext = filename.split('.').pop()?.toUpperCase() || 'FILE';

  return (
    <a
      href={src}
      target="_blank"
      rel="noopener noreferrer"
      className={cn('flex items-center gap-3 p-4 bg-muted/50 rounded-xl hover:bg-muted transition-colors', className)}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="h-12 w-12 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
        <FileText className="h-6 w-6 text-blue-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{filename}</p>
        <p className="text-xs text-muted-foreground">{ext} document</p>
      </div>
      <Download className="h-5 w-5 text-muted-foreground shrink-0" />
    </a>
  );
}

function MediaSkeleton({ compact, className, aspectRatio }: { compact?: boolean; className?: string; aspectRatio?: number }) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-none -mx-3 sm:-mx-4 animate-pulse',
        className,
      )}
    >
      <div
        className="w-full bg-muted"
        style={{
          aspectRatio: aspectRatio || 16 / 9,
          maxHeight: compact ? '24rem' : '37.5rem',
        }}
      />
    </div>
  );
}

function LazyImage({ src, alt, compact, aspectRatio }: { src: string; alt: string; compact?: boolean; aspectRatio?: number }) {
  const [loaded, setLoaded] = useState(false);
  const imgClass = cn('w-full', compact ? 'max-h-96 object-cover' : 'max-h-[600px] object-cover');

  return (
    <div className="relative">
      <div className={cn('w-full bg-muted', loaded ? 'hidden' : 'animate-pulse')} style={{ aspectRatio: aspectRatio || 16 / 9, maxHeight: compact ? '24rem' : '37.5rem' }} />
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        className={cn(imgClass, 'transition-opacity duration-300', loaded ? 'opacity-100' : 'opacity-0')}
      />
    </div>
  );
}

/**
 * Portrait-aware media wrapper (X/Twitter style).
 * Shows full media with object-contain over a blurred background
 * so vertical content isn't clipped.
 */
function PortraitMedia({
  children,
  bgSrc,
  aspectRatio,
  compact,
  isVideo,
}: {
  children: React.ReactNode;
  bgSrc: string;
  aspectRatio: number;
  compact?: boolean;
  isVideo?: boolean;
}) {
  const cappedRatio = Math.max(aspectRatio, 3 / 4);
  const maxH = compact ? '24rem' : '37.5rem';
  const blurClass = "invisible @sm/main:visible absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-50";

  return (
    <div
      className="relative w-full overflow-hidden @sm/main:bg-muted @sm/main:dark:bg-black"
      style={{ aspectRatio: cappedRatio, maxHeight: maxH }}
    >
      {/* Blurred background fill (wide containers only; narrow uses object-cover) */}
      {isVideo ? (
        <video src={bgSrc} muted playsInline autoPlay loop aria-hidden className={blurClass} />
      ) : (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={bgSrc} alt="" aria-hidden className={blurClass} />
      )}
      <div className="relative w-full h-full flex items-center justify-center">
        {children}
      </div>
    </div>
  );
}

/** Exported for use outside (e.g. to detect type for lightbox) */
export { detectMediaType, mimeToMediaType, AudioPlayer };

export function PostMedia({ mediaUrl, mediaType, thumbnailUrl, alt, title, compact, className, onMediaClick, mediaWidth, mediaHeight, priority, preloaded }: PostMediaProps) {
  const altText = alt || title || 'Post media';
  const url = mediaUrl || thumbnailUrl || '';
  const syncType = detectMediaType(url, mediaType);
  const [resolvedType, setResolvedType] = useState<string>(syncType);

  useEffect(() => {
    if (syncType !== 'unknown') {
      setResolvedType(syncType);
      return;
    }
    let cancelled = false;
    fetch(url, { method: 'HEAD' })
      .then((res) => {
        if (cancelled) return;
        const ct = res.headers.get('content-type');
        setResolvedType(ct ? mimeToMediaType(ct) : 'file');
      })
      .catch(() => { if (!cancelled) setResolvedType('file'); });
    return () => { cancelled = true; };
  }, [url, syncType]);

  if (!mediaUrl && !thumbnailUrl) return null;

  const ar = mediaWidth && mediaHeight ? mediaWidth / mediaHeight : undefined;

  // Skeleton while resolving type via HEAD request
  if (resolvedType === 'unknown') {
    return <MediaSkeleton compact={compact} className={className} aspectRatio={ar} />;
  }

  const effectiveType = resolvedType;
  const isPortrait = ar !== undefined && ar < 1;
  const isVisualMedia = effectiveType === 'video' || effectiveType === 'image' || effectiveType === 'gif' || effectiveType === '3d';
  const wrapperClass = cn(
    'overflow-hidden',
    isVisualMedia ? 'rounded-none -mx-3 @sm/main:-mx-4' : 'rounded-xl bg-muted',
    onMediaClick && 'cursor-pointer',
    className,
  );

  const handleClick = onMediaClick
    ? (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onMediaClick(e);
      }
    : undefined;

  switch (effectiveType) {
    case 'video':
      if (isPortrait) {
        return (
          <div className={wrapperClass}>
            <PortraitMedia bgSrc={thumbnailUrl || url} aspectRatio={ar} compact={compact} isVideo={!thumbnailUrl}>
              <VideoPlayer
                src={url}
                poster={thumbnailUrl || undefined}
                compact={compact}
                aspectRatio={ar}
                portrait
                preloaded={preloaded}
                className="h-full w-full"
                onClick={onMediaClick}
              />
            </PortraitMedia>
          </div>
        );
      }
      return (
        <div className={wrapperClass}>
          <VideoPlayer
            src={url}
            poster={thumbnailUrl || undefined}
            compact={compact}
            aspectRatio={ar}
            preloaded={preloaded}
            onClick={onMediaClick}
          />
        </div>
      );

    case 'audio':
      return <AudioPlayer src={url} thumbnail={thumbnailUrl} className={className} compact={compact} onMediaClick={onMediaClick} />;

    case '3d':
      return (
        <div className={wrapperClass}>
          <ModelViewer3D
            src={url}
            poster={thumbnailUrl || undefined}
            compact={compact}
            onClickOnly={onMediaClick}
          />
        </div>
      );

    case 'pdf':
      return (
        <div className={wrapperClass}>
          <PdfEmbed src={url} compact={compact} />
        </div>
      );

    case 'file':
    case 'document':
      return <DocumentLink src={url} className={className} />;

    case 'gif':
      return (
        <div className={wrapperClass} onClick={handleClick}>
          <LazyImage
            src={url}
            alt={altText}
            compact={compact}
            aspectRatio={ar}
          />
        </div>
      );

    case 'image':
      if (isPortrait) {
        return (
          <div className={wrapperClass} onClick={handleClick}>
            <PortraitMedia bgSrc={url} aspectRatio={ar} compact={compact}>
              <OptimizedImage
                src={url}
                alt={altText}
                width={mediaWidth || 600}
                height={mediaHeight || 800}
                className="w-full h-full object-cover @sm/main:object-contain"
                sizes={compact ? '(max-width: 640px) 100vw, 600px' : '(max-width: 640px) 100vw, 1200px'}
                loading={priority ? 'eager' : 'lazy'}
                priority={priority}
                placeholder="empty"
              />
            </PortraitMedia>
          </div>
        );
      }
      return (
        <div className={wrapperClass} onClick={handleClick}>
          <OptimizedImage
            src={url}
            alt={altText}
            width={compact ? 600 : 1200}
            height={compact ? 400 : 800}
            className={cn(
              'w-full',
              compact ? 'max-h-96 object-cover' : 'max-h-[600px] object-cover',
            )}
            sizes={compact ? '(max-width: 640px) 100vw, 600px' : '(max-width: 640px) 100vw, 1200px'}
            loading={priority ? 'eager' : 'lazy'}
            priority={priority}
            placeholder="empty"
          />
        </div>
      );

    default:
      return <DocumentLink src={url} className={className} />;
  }
}
